"use client";

/**
 * O35.P8 / P13.D — EasingField Prefer-over.
 * Emits EasingV1-compatible named/bezier values, or DialKit spring TransitionConfig.
 */

import { useId, useMemo } from "react";
import { DialKitEasing } from "../design-system/dialkit/dialkit-easing";
import { DialKitSelect } from "../design-system/dialkit/dialkit-select";
import { FieldShell, type FieldLayoutHint } from "./field-shell";
import { cn } from "../../lib";

export const EASING_NAMED_V1_OPTIONS = [
	"linear",
	"easeIn",
	"easeOut",
	"easeInOut",
	"easeInQuad",
	"easeOutQuad",
	"easeInOutQuad",
	"easeInCubic",
	"easeOutCubic",
	"easeInOutCubic",
	"circularArc",
] as const;

export type EasingNamedFieldValue = (typeof EASING_NAMED_V1_OPTIONS)[number];

export interface EasingFieldSpringValue {
	kind: "spring";
	stiffness?: number;
	damping?: number;
	mass?: number;
	visualDuration?: number;
	bounce?: number;
}

export type EasingFieldValue =
	| EasingNamedFieldValue
	| { kind: "bezier"; c1: number; c2: number }
	| EasingFieldSpringValue;

export interface EasingFieldProps {
	id?: string;
	label: string;
	value: EasingFieldValue;
	onChange: (value: EasingFieldValue) => void;
	disabled?: boolean;
	error?: string;
	description?: string;
	layoutHint?: FieldLayoutHint;
	/** named = Select of EasingNamedV1; advanced = DialKit TransitionControl (easing + spring). */
	mode?: "named" | "advanced";
	className?: string;
}

function isNamed(value: EasingFieldValue): value is EasingNamedFieldValue {
	return typeof value === "string";
}

function isSpring(value: EasingFieldValue): value is EasingFieldSpringValue {
	return typeof value === "object" && value.kind === "spring";
}

function isBezier(
	value: EasingFieldValue,
): value is { kind: "bezier"; c1: number; c2: number } {
	return typeof value === "object" && value.kind === "bezier";
}

/**
 * FieldType for construction along.easing / solidify knobs (P4/P9).
 * Advanced mode round-trips DialKit spring + easing (C26).
 */
export function EasingField({
	id: idProp,
	label,
	value,
	onChange,
	disabled = false,
	error,
	description,
	layoutHint,
	mode = "named",
	className,
}: EasingFieldProps) {
	const autoId = useId();
	const id = idProp ?? autoId;
	const options = useMemo(
		() =>
			EASING_NAMED_V1_OPTIONS.map((v) => ({
				value: v,
				label: v === "circularArc" ? "circularArc (dome)" : v,
			})),
		[],
	);

	const namedValue = isNamed(value) ? value : "linear";

	const advancedValue = isSpring(value)
		? {
				type: "spring" as const,
				stiffness: value.stiffness,
				damping: value.damping,
				mass: value.mass,
				visualDuration: value.visualDuration,
				bounce: value.bounce,
			}
		: isBezier(value)
			? {
					type: "easing" as const,
					duration: 0.3,
					ease: [value.c1, 0, value.c2, 1] as [
						number,
						number,
						number,
						number,
					],
				}
			: {
					type: "easing" as const,
					duration: 0.3,
					ease: [0.25, 0.1, 0.25, 1] as [
						number,
						number,
						number,
						number,
					],
				};

	return (
		<FieldShell
			id={id}
			label={label}
			error={error}
			description={description}
			layoutHint={layoutHint}
			className={cn(className)}
		>
			{mode === "named" ? (
				<div data-easing-field data-mode="named">
					<DialKitSelect
						label={label}
						value={namedValue}
						options={options}
						disabled={disabled}
						onChange={(next) => {
							if (
								(EASING_NAMED_V1_OPTIONS as readonly string[]).includes(
									next,
								)
							) {
								onChange(next as EasingNamedFieldValue);
							}
						}}
					/>
				</div>
			) : (
				<div data-easing-field data-mode="advanced">
					<DialKitEasing
						label={label}
						disabled={disabled}
						panelId={id}
						path="easing"
						value={advancedValue}
						onChange={(next) => {
							if (!next || typeof next !== "object" || !("type" in next)) {
								return;
							}
							if (next.type === "spring") {
								onChange({
									kind: "spring",
									stiffness: next.stiffness,
									damping: next.damping,
									mass: next.mass,
									visualDuration: next.visualDuration,
									bounce: next.bounce,
								});
								return;
							}
							if (
								next.type === "easing" &&
								Array.isArray(next.ease)
							) {
								onChange({
									kind: "bezier",
									c1: next.ease[0] ?? 0.25,
									c2: next.ease[2] ?? 0.25,
								});
							}
						}}
					/>
				</div>
			)}
		</FieldShell>
	);
}
